import apiService from 'app/apiService';
import type { APIResponseType } from 'app/apiService/types';

export interface AutomationNeedingAttentionResolveRequest {
  dealer_id: string;
  segment_name: string;
  reason_code: string;
  opportunity_type?: 'test' | 'active';
  schedule_at?: string;
  use_cadence?: boolean;
}

export interface AutomationNeedingAttentionRerunWebsocket {
  subscribe_job_topic?: string;
  subscribe_dealer_topic?: string;
  events?: string[];
}

export interface AutomationNeedingAttentionRerun {
  test_automation_id?: string;
  task_ids?: string[];
  opportunity_ids?: string[];
  items?: Array<{
    task_id?: string;
    opportunity_id?: string;
    lead_id?: string;
    record_type?: string;
  }>;
  websocket?: AutomationNeedingAttentionRerunWebsocket;
}

export interface AutomationNeedingAttentionResolveBody {
  segment_name?: string;
  reason_code?: string;
  rescheduled_count?: number;
  task_ids?: string[];
  failures?: Array<{ task_id?: string; error?: string }>;
  reruns?: AutomationNeedingAttentionRerun[];
  websocket?: AutomationNeedingAttentionRerunWebsocket;
}

class HumanInTheLoopApiService {
  private static readonly RESOLVE_ENDPOINT =
    'api/v2/nurturing-automation/automation-needing-attention/resolve';

  /**
   * `POST api/v2/nurturing-automation/automation-needing-attention/resolve?dealer_id=…`
   * Body: `dealer_id`, `segment_name`, `reason_code`, optional `opportunity_type`, etc.
   */
  static async postAutomationNeedingAttentionResolve(
    payload: AutomationNeedingAttentionResolveRequest,
  ): Promise<APIResponseType<AutomationNeedingAttentionResolveBody>> {
    const dealerId = payload.dealer_id?.trim();
    const segmentName = payload.segment_name?.trim();
    const reasonCode = payload.reason_code?.trim();
    if (!dealerId) {
      throw new Error('Dealer ID is required');
    }
    if (!segmentName) {
      throw new Error('Segment name is required');
    }
    if (!reasonCode) {
      throw new Error('Reason code is required');
    }

    const body: AutomationNeedingAttentionResolveRequest = {
      dealer_id: dealerId,
      segment_name: segmentName,
      reason_code: reasonCode,
    };
    if (payload.opportunity_type) {
      body.opportunity_type = payload.opportunity_type;
    }
    if (payload.schedule_at) {
      body.schedule_at = payload.schedule_at;
    }
    if (payload.use_cadence != null) {
      body.use_cadence = payload.use_cadence;
    }

    return apiService.post<AutomationNeedingAttentionResolveBody>(
      HumanInTheLoopApiService.RESOLVE_ENDPOINT,
      body,
      { params: { dealer_id: dealerId } },
      false,
    );
  }
}

export default HumanInTheLoopApiService;
